import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import SelectBox from 'components/form/SelectBox'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  fetchAdGroupKeywords,
  fetchCampaignAdGroups,
  fetchCampaigns,
  reset,
} from 'features/dashboard/operation/keywordCopy/copyKeywordsSlice'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import api from 'api/axios/foka'
import { equalTo, isPresent, not } from 'utils'
import Icon from 'components/Icon'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import Limitations from 'features/Limitations'
import CheckBox from 'components/form/CheckBox'
import NoneAnchor from 'components/NoneAnchor'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import { setSelectedIds } from 'features/dashboard/table/keyword/keywordTableSlice'

const { AD_GROUP_MAX_KEYWORDS } = Limitations

const CopyKeywordsDialog = ({ handleClose }) => {
  const dispatch = useDispatch()
  const [selectedCampaign, setCampaign] = useState(undefined)
  const [selectedAdGroup, setAdGroup] = useState(undefined)
  const [copyAsOff, setCopyAsOff] = useState(false)
  const {
    keywordTable: { content, selectedIds },
    copyKeywords: {
      campaigns,
      campaignAdGroups,
      targetAdGroupKeywords,
      loading,
    },
  } = useSelector(
    ({ dashboard }) => ({
      keywordTable: dashboard.table.keywordTable,
      copyKeywords: dashboard.operation.copyKeywords,
    }),
    shallowEqual
  )
  const selectedKeywords = content.filter(({ id }) => selectedIds.includes(id))
  const bizChannelId = content.find(({ id }) => id === selectedIds[0])
    ?.parentDomainInfo?.bizChannel?.id

  useEffect(() => {
    if (bizChannelId) {
      dispatch(fetchCampaigns(bizChannelId))
    }
  }, [bizChannelId, dispatch])
  useEffect(() => () => dispatch(reset()), [dispatch])

  const filteredKeywords = selectedKeywords.filter(
    ({ text }) => !targetAdGroupKeywords.find(keyword => keyword.text === text)
  )

  const registableCount = AD_GROUP_MAX_KEYWORDS - targetAdGroupKeywords.length

  const handleDeselect = deleteId => {
    dispatch(setSelectedIds(selectedIds.filter(not(equalTo(deleteId)))))
  }

  const handleSelectCampaign = v => {
    setCampaign(v)
    if (v) {
      dispatch(fetchCampaignAdGroups(v.id))
      setAdGroup(undefined)
    }
  }
  const handleSelectAdGroup = v => {
    setAdGroup(v)
    if (v) {
      dispatch(fetchAdGroupKeywords(v.id))
    }
  }

  const handleConfirm = async ({ preventClose }) => {
    if (registableCount < filteredKeywords.length) {
      preventClose()
      return fail(
        registableCount === 0
          ? `대상 광고그룹에 키워드를 ${AD_GROUP_MAX_KEYWORDS}개 초과하여 등록할 수 없습니다.`
          : `광고그룹에 이미 등록된 키워드로 인해 ${registableCount}개의 키워드만 등록가능합니다.\n` +
              `${registableCount}개 내에서 복사할 키워드를 다시 선택하세요.`
      )
    }

    try {
      await api.ad.copyKeywords({
        adGroupId: selectedAdGroup.id,
        keywordIds: filteredKeywords.map(keyword => keyword.id),
        copyAsOff,
      })
      success('선택한 키워드를 복사하였습니다.')
      dispatch(refreshDashboard())
    } catch (e) {
      preventClose()
      failWithErrorResponse(e?.response?.data, {
        messageMap: {
          104007: '이미 삭제된 그룹은 복사할 수 없습니다.',
          104010: '선택한 광고그룹이 삭제되어 복사할 수 없습니다.',
          105000: '선택한 키워드 중 삭제된 키워드가 있어 복사할 수 없습니다.',
          105012:
            '광고계정내 등록 가능한 키워드 개수가 초과 되었습니다.\n등록 가능 개수 내로 다시 선택하세요.',
        },
      })
    }
  }

  return (
    <ConfirmModal
      title="키워드 복사"
      onConfirm={handleConfirm}
      handleClose={handleClose}
      confirmDisabled={
        selectedAdGroup === undefined || filteredKeywords.length === 0
      }>
      <p className="desc_copy">
        <span className="fc_warn">캠페인 유형이 동일한 광고그룹</span>으로만
        복사 가능합니다.
        <br />
        프리미엄링크 키워드 복사시엔 설정된{' '}
        <span className="fc_warn">키워드입찰가와 랜딩 URL 모두 복사</span>
        됩니다.
      </p>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 키워드가 복사될 캠페인/광고그룹
          <HelpTooltip>
            비즈채널이 동일한 캠페인/광고그룹에 한해 복사 가능합니다
          </HelpTooltip>
        </strong>
        {campaigns.length === 0 && !loading ? (
          <p className="txt_info">
            <span className="ico_comm" />
            선택 가능한 캠페인이 존재하지 않습니다.
          </p>
        ) : (
          <LoadingWrapper isLoading={loading}>
            <SelectBox
              value={selectedCampaign}
              onValueChange={handleSelectCampaign}
              placeholder="캠페인 선택">
              {(campaigns || []).map(campaign => (
                <SelectBox.Item key={campaign.id} value={campaign}>
                  {campaign.name}
                </SelectBox.Item>
              ))}
            </SelectBox>
            <SelectBox
              value={selectedAdGroup}
              onValueChange={handleSelectAdGroup}
              disabled={
                loading ||
                selectedCampaign === undefined ||
                campaignAdGroups.length === 0
              }
              placeholder="광고그룹 선택">
              {campaignAdGroups.map(adGroup => (
                <SelectBox.Item key={adGroup.id} value={adGroup}>
                  {adGroup.name}
                </SelectBox.Item>
              ))}
            </SelectBox>
          </LoadingWrapper>
        )}
      </div>
      <div className="select_ad">
        <strong className="tit_copy">
          선택한 키워드&nbsp;
          <span
            className={
              registableCount < filteredKeywords.length ? 'fc_warn' : 'fc_emph'
            }>
            {filteredKeywords.length}
          </span>
        </strong>
        <div className="list_keyword">
          {isPresent(filteredKeywords) ? (
            filteredKeywords.map(keyword => (
              <span key={keyword.id} className="txt_keyword">
                {keyword.text}
                <NoneAnchor
                  className="link_del"
                  onClick={() => handleDeselect(keyword.id)}>
                  <Icon name="del">삭제</Icon>
                </NoneAnchor>
              </span>
            ))
          ) : (
            <span className="fc_info">
              대상 광고그룹에 존재하는 키워드를 제외하고 복사 가능한 키워드가
              없습니다.
            </span>
          )}
        </div>
      </div>
      <div className="select_ad">
        <strong className="tit_copy">추가 설정</strong>
        <CheckBox
          label="키워드 OFF 상태로 복사"
          checked={copyAsOff}
          onCheckedChange={checked => {
            setCopyAsOff(checked)
          }}
        />
      </div>
    </ConfirmModal>
  )
}

CopyKeywordsDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
}

export default CopyKeywordsDialog
